import React, { useMemo } from 'react';
import EmptyState from '../../components/ui/EmptyState';
import {
  EmployeeDashboardGrid,
  EmployeeDetailList,
  EmployeeSummaryCard,
  EmployeeWorkspaceEmptyState,
  EmployeeWorkspaceSection
} from '../../components/workforce/EmployeeWorkspace';
import { certificationName, classifyCertification } from './certificationStatus';

function formatDate(value) {
  if (!value) return null;
  const dateOnly = String(value).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return dateOnly;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value).slice(0, 10) : date.toLocaleDateString();
}

function formatTenure(dateOfHire, now = new Date()) {
  if (!dateOfHire) return 'Not recorded';
  const dateOnly = String(dateOfHire).slice(0, 10);
  const dateParts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateOnly);
  const hired = dateParts
    ? new Date(Number(dateParts[1]), Number(dateParts[2]) - 1, Number(dateParts[3]))
    : new Date(dateOfHire);
  if (Number.isNaN(hired.getTime()) || hired > now) return 'Not available';
  let months = (now.getFullYear() - hired.getFullYear()) * 12 + now.getMonth() - hired.getMonth();
  if (now.getDate() < hired.getDate()) months -= 1;
  const years = Math.floor(Math.max(0, months) / 12);
  const remainder = Math.max(0, months) % 12;
  if (years && remainder) return `${years} yr ${remainder} mo`;
  if (years) return `${years} ${years === 1 ? 'year' : 'years'}`;
  return `${remainder} ${remainder === 1 ? 'month' : 'months'}`;
}

export default function EmployeeCareerWorkspace({
  profile,
  documents = [],
  canReadDocuments = false,
  supervisorLabel = 'Unassigned',
  locationLabel = null
}) {
  const certifications = useMemo(() => (
    canReadDocuments
      ? documents.filter(document => document.documentType === 'certification')
      : []
  ), [canReadDocuments, documents]);

  const certificationRows = useMemo(() => certifications.map(document => ({
    ...document,
    careerStatus: classifyCertification(document)
  })), [certifications]);

  const activeCount = certificationRows.filter(row => row.careerStatus === 'Active').length;
  const expiringCount = certificationRows.filter(row => row.careerStatus === 'Expiring').length;
  const archivedCount = certificationRows.filter(row => row.careerStatus === 'Archived').length;
  const timeline = useMemo(() => {
    const entries = [];
    if (profile?.dateOfHire) {
      entries.push({ id: 'hire', date: profile.dateOfHire, type: 'Hire', label: 'Employment start date' });
    }
    if (canReadDocuments) certificationRows.forEach(document => {
      const date = document.uploadedAt || document.createdAt;
      if (date) entries.push({
        id: `certification-${document.id}`,
        date,
        type: 'Certification',
        label: `${certificationName(document)} recorded`
      });
    });
    return entries.sort((left, right) => String(right.date).localeCompare(String(left.date)));
  }, [canReadDocuments, certificationRows, profile?.dateOfHire]);

  return (
    <div className="employee-career" data-testid="employee-career-workspace">
      <EmployeeWorkspaceSection title="Career summary" description="Current employee profile and authorized certification records">
        <EmployeeDashboardGrid columns="wide">
          <EmployeeSummaryCard label="Current role" value={profile?.jobTitle || profile?.role || 'Not recorded'} detail={profile?.department || 'Department not recorded'} />
          <EmployeeSummaryCard label="Tenure" value={formatTenure(profile?.dateOfHire)} detail={profile?.dateOfHire ? `Hired ${formatDate(profile.dateOfHire)}` : 'Hire date not recorded'} />
          <EmployeeSummaryCard label="Certifications" value={canReadDocuments ? certificationRows.filter(row => row.careerStatus !== 'Archived').length : 'Restricted'} detail={canReadDocuments ? 'Current non-archived records' : 'Document access required'} />
        </EmployeeDashboardGrid>
        <EmployeeDetailList items={[
          { label: 'Department', value: profile?.department },
          { label: 'Manager', value: supervisorLabel },
          { label: 'Location', value: locationLabel },
          { label: 'Career aspirations', value: profile?.careerAspirations }
        ]} />
      </EmployeeWorkspaceSection>

      <EmployeeWorkspaceSection title="Career timeline" description="Recorded employee milestones only">
        {timeline.length ? (
          <ol className="employee-career__timeline" data-testid="employee-career-timeline">
            {timeline.map(entry => (
              <li key={entry.id}>
                <time dateTime={String(entry.date)}>{formatDate(entry.date)}</time>
                <div><strong>{entry.type}</strong><span>{entry.label}</span></div>
              </li>
            ))}
          </ol>
        ) : (
          <EmptyState compact icon="·" headline="No career timeline available" body="Hire and certification events will appear when those records exist for this employee." />
        )}
      </EmployeeWorkspaceSection>

      <EmployeeWorkspaceSection title="Certifications" description="Authorized certification documents for this employee">
        {!canReadDocuments ? (
          <EmptyState compact icon="·" headline="Certification access restricted" body="Certification records require employee document permission." />
        ) : certificationRows.length ? (
          <>
            <EmployeeDashboardGrid columns="wide">
              <EmployeeSummaryCard label="Active" value={activeCount} />
              <EmployeeSummaryCard label="Expiring" value={expiringCount} tone={expiringCount ? 'warning' : 'neutral'} />
              <EmployeeSummaryCard label="Archived" value={archivedCount} />
            </EmployeeDashboardGrid>
            <ul className="employee-career__records" data-testid="employee-career-certifications">
              {certificationRows.map(document => (
                <li key={document.id}>
                  <strong>{certificationName(document)}</strong>
                  <span>{document.careerStatus}{document.expiresAt ? ` · expires ${formatDate(document.expiresAt)}` : ''}</span>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <EmptyState compact icon="·" headline="No certifications on file" body="No certification documents are recorded for this employee." />
        )}
        {canReadDocuments ? (
          <div className="employee-career__truthful-state" data-testid="employee-career-missing-certifications">
            <strong>Missing certifications</strong>
            <span>Certification requirements are not configured, so missing certifications cannot be determined.</span>
          </div>
        ) : null}
      </EmployeeWorkspaceSection>

      <EmployeeDashboardGrid>
        <EmployeeWorkspaceSection title="Training" description="Planned capability">
          <EmployeeWorkspaceEmptyState title="Training history is planned" description="The current data model does not contain completed learning or in-progress courses." />
        </EmployeeWorkspaceSection>
        <EmployeeWorkspaceSection title="Development plan" description="Planned capability">
          <EmployeeWorkspaceEmptyState title="Development plans are planned" description="The current data model does not contain development plans or development actions." />
        </EmployeeWorkspaceSection>
        <EmployeeWorkspaceSection title="Promotion readiness" description="Truthful inputs only — no numeric assessment">
          <EmployeeWorkspaceEmptyState title="Promotion readiness inputs are planned" description="The current model does not define role requirements, competencies, required learning, or promotion criteria." />
        </EmployeeWorkspaceSection>
        <EmployeeWorkspaceSection title="Career conversations" description="Planned capability">
          <EmployeeWorkspaceEmptyState title="Career conversations are planned" description="Coaching notes are not classified as career-development conversations in the current model." />
        </EmployeeWorkspaceSection>
      </EmployeeDashboardGrid>

      <EmployeeWorkspaceSection title="Future AI career guidance" description="Reserved capability — no AI processing is active">
        <EmployeeWorkspaceEmptyState title="AI career guidance is planned" description="Future governed capabilities may include development recommendations, learning suggestions, and promotion preparation." />
      </EmployeeWorkspaceSection>
    </div>
  );
}

export { classifyCertification, formatTenure };
